import { StaticPageLayout } from '../layouts/static-page-layout';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, Lock, Eye, Users, FileText, Clock, Mail, Phone } from 'lucide-react';
import { useEffect } from 'react';

export function PrivacyPolicyPage() {
  // Set page title
  useEffect(() => {
    document.title = 'Privacy Policy - AceHorizon';
  }, []);
  const privacySections = [
    {
      icon: FileText,
      title: "Information We Collect",
      gradient: "from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20",
      content: {
        intro: "We may collect the following types of information:",
        items: [
          "Personal Information: Name, email address, phone number, job title, company name, and other contact details you provide",
          "Service Usage Information: Information related to the AI educational services you request or we provide",
          "Technical Information: IP address, browser type, device information, pages visited, time spent on the website",
          "Communication Records: Records of email communications, inquiries, support requests, and feedback"
        ]
      }
    },
    {
      icon: Shield,
      title: "How We Use Your Information",
      gradient: "from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20",
      content: {
        items: [
          "To provide, maintain, and improve our AI educational services",
          "To respond to your inquiries, comments, or questions",
          "To send you technical notices, updates, security alerts, and administrative messages",
          "To communicate about new services, features, or offers",
          "To monitor and analyze trends, usage, and activities",
          "To protect against fraud and unauthorized transactions"
        ]
      }
    },
    {
      icon: Users,
      title: "Information Sharing and Disclosure",
      gradient: "from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20",
      content: {
        intro: "We may share your personal information in the following circumstances:",
        items: [
          "Service Providers: With third-party vendors who need access to help provide our services",
          "Legal Requirements: If required by law or valid requests from authorities",
          "Business Transfers: In connection with any merger, sale, or acquisition",
          "With Your Consent: When you have agreed to the sharing"
        ]
      }
    },
    {
      icon: Lock,
      title: "Data Security",
      gradient: "from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20",
      content: {
        text: "We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the Internet is 100% secure. While we strive to protect your personal information, we cannot guarantee its absolute security."
      }
    },
    {
      icon: Eye,
      title: "Cookies and Similar Technologies",
      gradient: "from-teal-50 to-teal-100 dark:from-teal-900/20 dark:to-teal-800/20",
      content: {
        text: "We use cookies and similar tracking technologies to track activity on our website. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, this may affect your ability to use some portions of our website."
      }
    }
  ];

  const userRights = [
    "The right to access personal information we hold about you",
    "The right to request correction of inaccurate information",
    "The right to request deletion of your information",
    "The right to object to processing of your information",
    "The right to data portability"
  ];

  return (
    <StaticPageLayout>
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          
          {/* Hero Section */}
          <div className="text-center py-5 sm:py-12">
            <div className="max-w-4xl mx-auto px-2 sm:px-4">
              <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight mb-6 bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600 dark:from-cyan-400 dark:via-blue-500 dark:to-purple-500 bg-clip-text text-transparent leading-tight px-2">
                Privacy Policy
              </h1>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed px-2">
                This Privacy Policy describes how <span className="text-primary font-semibold">Ace Horizon</span> collects, uses, and shares your 
                personal information when you visit our website, contact us for services, or engage with our 
                <span className="text-primary font-semibold"> AI educational services</span>.
              </p>
              <div className="text-sm text-muted-foreground">
                <Clock className="inline-block w-4 h-4 mr-2" />
                Last Updated: April 15, 2025
              </div>
            </div>
          </div>

          {/* Privacy Sections */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 dark:from-emerald-400 dark:via-teal-400 dark:to-cyan-400 bg-clip-text text-transparent text-center">
              Our Privacy Practices
            </h2>
            
            <div className="space-y-8">
              {privacySections.map((section, index) => {
                const IconComponent = section.icon;
                return (
                  <Card key={index} className={`border-0 bg-gradient-to-br ${section.gradient} transition-all duration-300 hover:shadow-lg`}>
                    <CardHeader>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                          <IconComponent className="h-6 w-6 text-white" />
                        </div>
                        <CardTitle className="text-xl sm:text-2xl font-bold">
                          {index + 1}. {section.title}
                        </CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {section.content.intro && (
                        <p className="text-base sm:text-lg mb-4 text-muted-foreground">
                          {section.content.intro}
                        </p>
                      )}
                      {section.content.text && (
                        <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                          {section.content.text}
                        </p>
                      )}
                      {section.content.items && (
                        <ul className="space-y-3">
                          {section.content.items.map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-start gap-3 text-base sm:text-lg text-muted-foreground">
                              <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Your Rights Section */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 dark:from-violet-400 dark:via-purple-400 dark:to-fuchsia-400 bg-clip-text text-transparent text-center">
              Your Rights
            </h2>
            <Card className="border-0 bg-gradient-to-br from-violet-50 to-purple-100 dark:from-violet-900/20 dark:to-purple-800/20">
              <CardHeader>
                <CardTitle className="text-xl sm:text-2xl font-bold text-center">
                  Depending on your location, you may have certain rights regarding your personal information:
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {userRights.map((right, index) => (
                    <div key={index} className="flex items-start gap-3 p-4 bg-white/50 dark:bg-black/20 rounded-lg">
                      <Shield className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-base sm:text-lg text-muted-foreground">{right}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Additional Sections */}
          <div className="mb-16 space-y-8">
            {/* Children's Privacy */}
            <Card className="border-0 bg-gradient-to-br from-orange-50 to-red-100 dark:from-orange-900/20 dark:to-red-800/20">
              <CardHeader>
                <CardTitle className="text-xl sm:text-2xl font-bold flex items-center gap-3">
                  <Users className="h-6 w-6 text-orange-600" />
                  Student Privacy & Parental Rights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-4">
                  Our educational platform is specifically designed for 9th class and above students, typically aged above 14 years. 
                  We recognize that many of our users are minors and we take special care to protect their privacy and safety.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-base sm:text-lg text-muted-foreground">
                      <strong>Parental Consent:</strong> We require parental or guardian consent before collecting personal 
                      information from users under 18 years of age.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-base sm:text-lg text-muted-foreground">
                      <strong>Limited Data Collection:</strong> We only collect information necessary to provide educational 
                      services and track academic progress.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-base sm:text-lg text-muted-foreground">
                      <strong>Enhanced Security:</strong> Student accounts have additional privacy protections and parental 
                      oversight features.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-base sm:text-lg text-muted-foreground">
                      <strong>Educational Purpose:</strong> All data collected is used solely for educational purposes and 
                      improving learning outcomes.
                    </p>
                  </div>
                </div>
                <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mt-4">
                  If you are a parent or guardian and have concerns about your child's use of our platform or the 
                  information we collect, please contact us immediately at <b><em>support@acehorizon.ai</em></b>.
                </p>
              </CardContent>
            </Card>

            {/* Changes to Privacy Policy */}
            <Card className="border-0 bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-900/20 dark:to-emerald-800/20">
              <CardHeader>
                <CardTitle className="text-xl sm:text-2xl font-bold flex items-center gap-3">
                  <FileText className="h-6 w-6 text-green-600" />
                  Changes to Privacy Policy
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                  We may update our Privacy Policy periodically. We will notify you of any changes by posting the new 
                  Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this 
                  Privacy Policy periodically for any changes.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Contact Section */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 bg-gradient-to-r from-indigo-600 via-blue-600 to-teal-600 dark:from-indigo-400 dark:via-blue-400 dark:to-teal-400 bg-clip-text text-transparent text-center">
              Contact Us About Privacy
            </h2>
            <Card className="border-0 bg-gradient-to-br from-indigo-50 to-blue-100 dark:from-indigo-900/20 dark:to-blue-800/20">
              <CardContent className="p-8">
                <p className="text-base sm:text-lg text-muted-foreground mb-6 text-center">
                  If you have any questions about this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                  <div className="flex items-center gap-3 p-4 bg-white/50 dark:bg-black/20 rounded-lg">
                    <Mail className="h-6 w-6 text-primary" />
                    <div>
                      <p className="font-semibold">Email</p>
                      <a href="mailto:support@acehorizon.ai" className="text-primary hover:underline">
                        support@acehorizon.ai
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-white/50 dark:bg-black/20 rounded-lg">
                    <Phone className="h-6 w-6 text-primary" />
                    <div>
                      <p className="font-semibold">Phone</p>
                      <p className="text-muted-foreground">+91 9596413917</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </StaticPageLayout>
  );
}
